import React, { FC, useEffect, useMemo } from "react";
import moment from "moment";
import CustomTable from "../../../components/Tables/CustomTable";
import ClickableUserName from "../../../components/shared/ClickableUserName";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import { fetchCashbackIssuances } from "../../../redux/features/cashbackSlice";
import { CashbackIssuance } from "../../../apis/cashback";
import CouponFilterBar from "./CouponFilterBar";
import { useCouponFilters } from "./useCouponFilters";
import { buildListCouponQuery } from "./buildListCouponQuery";

const formatNaira = (amount: number) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 0,
  }).format(amount ?? 0);

const renderBuyer = (buyer: CashbackIssuance["buyer"] | undefined) => {
  if (!buyer) return <span className="text-gray-400">—</span>;
  return (
    <div className="flex flex-col min-w-0">
      <ClickableUserName user={buyer} userType="BUYER" />
      {buyer.email && (
        <span className="text-xs text-gray-500 truncate" title={buyer.email}>
          {buyer.email}
        </span>
      )}
    </div>
  );
};

const CashbackIssuancesTable: FC = () => {
  const dispatch = useAppDispatch();
  const { issuances, loading } = useAppSelector((state) => state.cashback);

  const filters = useCouponFilters();
  const { page, setPage, setLimit, hasActiveFilters } = filters;

  // Cashback issuances ignore couponType/status/discountType/expiry — the
  // endpoint copy-pasted the full coupon filter set but none of those concepts
  // apply to wallet credits. Build a scoped query so filters set on a different
  // tab (e.g. couponType=REFERRAL from All Coupons) don't bleed into this fetch.
  const cashbackQuery = useMemo(
    () =>
      buildListCouponQuery({
        page: filters.page,
        limit: filters.limit,
        search: filters.search,
        buyerName: filters.buyerName,
        createdRange: filters.createdRange,
      }),
    [filters.page, filters.limit, filters.search, filters.buyerName, filters.createdRange]
  );

  useEffect(() => {
    dispatch(fetchCashbackIssuances(cashbackQuery));
  }, [dispatch, cashbackQuery]);

  const tableData = useMemo(
    () => ({
      columns: [
        { label: "Code", field: "code" },
        { label: "Buyer", field: "buyer" },
        { label: "Cashback Amount", field: "amount" },
        { label: "Issued By", field: "issuedBy" },
        { label: "Date Issued", field: "dateIssued" },
      ],
      rows: (issuances?.items ?? []).map((item: CashbackIssuance) => ({
        id: item.id,
        code: (
          <span className="font-mono font-medium text-gray-800">
            {item.couponCode}
          </span>
        ),
        buyer: renderBuyer(item.buyer),
        amount: (
          <span className="font-semibold text-gray-800">
            {formatNaira(item.cashbackAmount)}
          </span>
        ),
        issuedBy: (
          <span className="text-gray-700">
            {item.issuedBy?.fullName || "—"}
          </span>
        ),
        dateIssued: item.dateIssued ? (
          <span
            className="text-gray-600"
            title={moment(item.dateIssued).format("MMM D, YYYY h:mm A")}
          >
            {moment(item.dateIssued).format("Do MMM YYYY, h:mm A")}
          </span>
        ) : (
          <span className="text-gray-400">—</span>
        ),
      })),
    }),
    [issuances]
  );

  return (
    <>
      <CouponFilterBar
        filters={filters}
        panelId="cashback-filter-panel"
        visibleFilters={["search", "buyerName", "createdRange"]}
      />

      <CustomTable
        data={tableData}
        noRecordText={
          hasActiveFilters
            ? "No cashback issuances match the current filters."
            : "No cashback has been issued yet."
        }
        loading={loading}
        showFooter={true}
        totalPages={issuances?.meta?.totalPages}
        itemsPerPage={issuances?.meta?.itemsPerPage}
        currentPage={page}
        onPageChange={(value: number) => setPage(value)}
        onLimitChange={(value: number) => {
          setLimit(value);
          setPage(0);
        }}
      />
    </>
  );
};

export default CashbackIssuancesTable;
